import { BaseControl } from '@Common/Controls/BaseControl';
import { GridLayout } from '@Common/Controls/GridLayout';
import { Label } from '@Common/Controls/Label';
import { Popup } from '@Common/Controls/Popup';
import constants from '@Common/Core/Constant';

import Maybe from '@Common/Core/Maybe';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import StylesHelper from '@Common/Helpers/StylesHelper';
import { SlidePopupAppearance, TextStyle } from '@InstanceFactory';
import { enumerable } from '@Common/Core/CoreDecorator';
import { safeDestroys } from '@Common/Core/Destroyable';

export class SlidePopupParams {
    public popupAppearance: SlidePopupAppearance;
    public content: BaseControl;
    public stylesHelper: StylesHelper;
    public withOverlay? = true;
}

const animationSpeed = 'fast';

export class SlidePopup {
    private readonly popup: Popup;
    private readonly root: GridLayout;
    private overlay;
    private readonly back: JQuery;
    private readonly title: Label;
    private readonly eventsBag = new MulticastDelegateBag();
    public readonly onClose = this.eventsBag.create<IHandler>();

    constructor(private readonly settings: SlidePopupParams) {
        _.defaultsDeep(this.settings, new SlidePopupParams());
        this.back = $('<div class="arrow-left"/>');
        const titleClass = settings.stylesHelper.getStyleName(settings.popupAppearance.titleStyle);
        const popupStyle = settings.stylesHelper.getStyleName(settings.popupAppearance.popupStyle);
        this.title = new Label({
            caption: this.settings.popupAppearance.title || '',
            className: `slide-popup-title ${titleClass || ''}`,
            css: { padding: 0, background: 'none' }
        });
        this.root = new GridLayout({
            style: { tableLayout: 'fixed', width: this.width },
            columns: [{ style: { width: '30px' } }, { style: { width: '*' } }],
            rows: [
                {
                    className: titleClass,
                    style: { minHeight: '30px', height: 'auto' }
                },
                { style: { verticalAlign: 'top', height: '*' } }
            ],
            cells: [
                {
                    rowIndex: 0,
                    columnIndex: 0,
                    className: 'no-gap-bottom gap-2-left',
                    content: this.back,
                    onClick: () => this.hide()
                },
                {
                    rowIndex: 0,
                    columnIndex: 1,
                    content: this.title,
                    className: `${titleClass} slide-popup-title-container`
                },
                { rowIndex: 1, colSpan: 2, columnIndex: 0, content: this.settings.content }
            ]
        });

        this.popup = new Popup({
            className: `slide-popup ${popupStyle}`,
            content: this.root.getControl(),
            isHidden: true
        });
        this.popup.onClose(() => {
            this.overlay && this.overlay.close();
            this.onClose();
        });
    }

    @enumerable
    private get width(): number {
        return this.settings.popupAppearance.width || window.innerWidth;
    }

    @enumerable
    private get popupElement(): JQuery {
        return this.popup.getControl();
    }

    @enumerable
    private get popupPosition(): number {
        return this.popup.getControl().offset().left;
    }

    private stopAnimation(): void {
        this.popupElement.stop();
    }

    public show(): void {
        this.stopAnimation();
        if (this.settings.withOverlay) {
            this.overlay = commonUtils.createPopUp({ opacity: true });
        }
        this.showInner();
        const { width, height } = this.settings.popupAppearance;
        this.adjust(width, height);
    }

    public adjust(w: number, h: number): void {
        h = h || window.innerHeight;
        w = w || window.innerWidth;
        const style = this.settings.stylesHelper.getOriginalStyle(this.settings.popupAppearance.popupStyle) as TextStyle;
        const paddings = Maybe(style.padding);
        const offsetH = paddings.get('top').valueOrDefault(0) + paddings.get('bottom').valueOrDefault(0);
        const offsetW = paddings.get('left').valueOrDefault(0) + paddings.get('right').valueOrDefault(0);
        this.root.adjust(w - offsetW, h - offsetH);
    }

    public hide(needRemove = false): void {
        if (!this.popup.isVisible) {
            return;
        }

        if (this.settings.popupAppearance.useSlideEffect) {
            this.stopAnimation();
            this.popupElement.animate({ left: -this.width }, animationSpeed, () => this.instantHide(needRemove));
        } else {
            this.instantHide(needRemove);
        }
    }

    public destroy(): void {
        safeDestroys(this.eventsBag, this.popup, this.root);
    }

    private showInner(startPosition: number = this.width): void {
        if (this.settings.popupAppearance.useSlideEffect) {
            this.popup.show(-startPosition, 0);
            this.popupElement.animate({ left: 0 }, animationSpeed, () => {
                this.makeDraggable();
            });
        } else {
            this.popup.show(0, 0);
        }
    }

    private makeDraggable(): void {
        this.popupElement.touchMove(leftToRight => {
            if (!leftToRight) {
                this.hide();
            }
        }, constants.touchMoveTitleLength);
    }

    private instantHide(remove: boolean): void {
        this.stopAnimation();
        this.overlay && this.overlay.close();
        this.popup.hide();
        remove && this.destroy();
    }
}
